<template>
    <div class="modal-card">
        <header class="modal-card-head">
            <p class="modal-card-title">Adjust Metric Weights</p>
            <b-field>
                <b-input rounded v-model="searchTerm" placeholder="search..." icon="search"></b-input>
            </b-field>
        </header>
        <section class="modal-card-body">
            <div class="weight-container">
                <b-notification type="is-danger" v-if="errorMessage" :closable="false">
                    {{ errorMessage }}
                </b-notification>
                <span class="icon is-small is-loading" v-if="isLoading">
                    <i class="fas fa-spinner fa-pulse"></i>&nbsp;Loading Weights...
                </span>

                <span class="heading">
                    Pillar
                    <span class="is-pulled-right">
                        Total: {{sumPMetrics}}%
                    </span>
                </span>
                <ul>
                    <li v-for="topic in filteredPMetrics" :key="topic.id" class="weight-item">
                        <span class="name">
                            {{topic.metric}}
                            <span class="tag">{{topic.platform}}</span>
                        </span>
                        <!-- The 'validity.valid' property will be true if and only if the input falls within the range of 0(min) to 100(max).
                             If the input value is invalid or undefined then set the value to 0.
                        -->
                        <span v-if="isReadOnly" class="is-pulled-right">
                            {{topic.weight}}
                        </span>
                        <input v-if="!isReadOnly" type="number" min="0" max="100" oninput="(validity.valid && this.value) || (value = 0)"
                        v-model.number="topic.weight" class="input is-pulled-right" width=120 :class="sumPMetrics > 100 ? 'is-danger' : ''" />
                    </li>
                </ul>

                <!-- Display metric weights separated by axis -->
                <span class="heading has-margin-above">
                    Conversation
                    <span class="is-pulled-right">
                        Total: {{sumXMetrics}}%
                    </span>
                </span>
                <ul>
                    <li v-for="topic in filteredXMetrics" :key="topic.id" class="weight-item">
                        <span class="name">
                            {{topic.metric}}
                            <span class="tag">{{topic.platform}}</span>
                        </span>
                        <!-- The 'validity.valid' property will be true if and only if the input falls within the range of 0(min) to 100(max).
                             If the input value is invalid or undefined then set the value to 0.
                        -->
                        <span v-if="isReadOnly" class="is-pulled-right">
                            {{topic.weight}}
                        </span>
                        <input v-if="!isReadOnly" type="number" min="0" max="100" oninput="(validity.valid && this.value) || (value = 0)"
                        v-model.number="topic.weight" class="input is-pulled-right" width=120 :class="sumXMetrics > 100 ? 'is-danger' : ''" />
                    </li>
                </ul>

                <span class="heading has-margin-above">
                    Quality
                    <span class="is-pulled-right">
                        Total: {{sumYMetrics}}%
                    </span>
                </span>
                <ul>
                    <li v-for="topic in filteredYMetrics" :key="topic.id" class="weight-item">
                        <span class="name">
                            {{topic.metric}}
                            <span class="tag">{{topic.platform}}</span>
                        </span>
                        <!-- The 'validity.valid' property will be true if and only if the input falls within the range of 0(min) to 100(max).
                             If the input value is invalid or undefined then set the value to 0.
                        -->
                        <span v-if="isReadOnly" class="is-pulled-right">
                            {{topic.weight}}
                        </span>
                        <input v-if="!isReadOnly" type="number" min="0" max="100" oninput="(validity.valid && this.value) || (value = 0)"
                        v-model.number="topic.weight" class="input is-pulled-right" width=120 :class="sumYMetrics > 100 ? 'is-danger' : ''" />
                    </li>
                </ul>

                <span class="heading has-margin-above">
                    Community
                    <span class="is-pulled-right">
                        Total: {{sumZMetrics}}%
                    </span>
                </span>
                <ul>
                    <li v-for="topic in filteredZMetrics" :key="topic.id" class="weight-item">
                        <span class="name">
                            {{topic.metric}}
                            <span class="tag">{{topic.platform}}</span>
                        </span>
                        <!-- The 'validity.valid' property will be true if and only if the input falls within the range of 0(min) to 100(max).
                             If the input value is invalid or undefined then set the value to 0.
                        -->
                        <span v-if="isReadOnly" class="is-pulled-right">
                            {{topic.weight}}
                        </span>
                        <input v-if="!isReadOnly" type="number" min="0" max="100" oninput="(validity.valid && this.value) || (value = 0)"
                        v-model.number="topic.weight" class="input is-pulled-right" width=120 :class="sumZMetrics > 100 ? 'is-danger' : ''" />
                    </li>
                </ul>

            </div>
        </section>
        <footer class="modal-card-foot">
            <button v-if="!isReadOnly" type="button" class="button is-pushed-left" @click="resetToDefaults">Reset to Default Weights</button>
            <button type="button" class="button" @click="close">Close</button>
            <button v-if="!isReadOnly" type="button" class="button is-primary" @click="applyMetricAdjustment">Accept Adjustment</button>
        </footer>
    </div>
</template>

<script>
import DefaultMetricWeightService from '@/services/default-metric-weight-service';
import _ from 'lodash';
import constants from '@/constants.js';

export default {
    name: 'ProjectWeightAdjustment',
    props: {
        inputMetricWeights: {
            type: Array,
            required: false
        },
        inputPillarWeights: {
            type: Array,
            required: false
        },
        isReadOnly: {
            type: Boolean,
            required: false
        }
    },
    data () {
        return {
            defaultMetricWeights: [],
            defaultPillarWeights: [],
            metricWeights: [],
            pillarWeights: [],
            errorMessage: '',
            isLoading: true,
            searchTerm: ''
        };
    },
    async created () {
        this.metricWeights = this.inputMetricWeights
            ? clone(this.inputMetricWeights)
            : [];
        this.pillarWeights = this.inputPillarWeights
            ? clone(this.inputPillarWeights)
            : [];

        try {
            this.defaultMetricWeights = (await DefaultMetricWeightService.getDefaultMetricWeights()).data;
            this.defaultPillarWeights = (await DefaultMetricWeightService.getDefaultPillarWeights()).data;

            if (!this.metricWeights || this.metricWeights.length <= 0) {
                this.metricWeights = clone(this.defaultMetricWeights);
            }
            if (!this.pillarWeights || this.pillarWeights.length <= 0) {
                this.pillarWeights = clone(this.defaultPillarWeights);
            }
        } catch (error) {
            this.errorMessage = error.message;
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        applyMetricAdjustment () {
            this.$emit('projectWeightsChanged', this.metricWeights, this.pillarWeights);
            this.$parent.close();
        },
        close () {
            this.$parent.close();
        },
        resetToDefaults () {
            this.metricWeights = clone(this.defaultMetricWeights);
            this.pillarWeights = clone(this.defaultPillarWeights);
        }
    },
    computed: {
        filteredXMetrics () {
            return this.metricWeights.filter(m => m.axis === constants.AXIS.X.key && weightContains(m, this.searchTerm));
        },
        filteredYMetrics () {
            return this.metricWeights.filter(m => m.axis === constants.AXIS.Y.key && weightContains(m, this.searchTerm));
        },
        filteredZMetrics () {
            return this.metricWeights.filter(m => m.axis === constants.AXIS.Z.key && weightContains(m, this.searchTerm));
        },
        filteredPMetrics () {
            return this.pillarWeights.filter(m => m.axis === constants.AXIS.P.key && weightContains(m, this.searchTerm));
        },

        sumXMetrics () {
            // Ignore undefined when summing
            return _.sumBy(this.metricWeights.filter(m => m.axis === constants.AXIS.X.key), m => m.weight || 0);
        },
        sumYMetrics () {
            // Ignore undefined when summing
            return _.sumBy(this.metricWeights.filter(m => m.axis === constants.AXIS.Y.key), m => m.weight || 0);
        },
        sumZMetrics () {
            // Ignore undefined when summing
            return _.sumBy(this.metricWeights.filter(m => m.axis === constants.AXIS.Z.key), m => m.weight || 0);
        },
        sumPMetrics () {
            // Ignore undefined when summing
            return _.sumBy(this.pillarWeights.filter(m => m.axis === constants.AXIS.P.key), m => m.weight || 0);
        }
    }
};

// Helper functions
let clone = (original) => JSON.parse(JSON.stringify(original));
let weightContains = (weight, searchTerm) => contains(weight.metric, searchTerm) || contains(weight.platform, searchTerm);
let contains = (item, searchTerm) => !item ? false : item.toString().toLowerCase().includes(searchTerm.toLowerCase());
</script>

<style lang="scss" scoped>
.weight-container {
    min-height: 250px;

    .weight-item {
        padding: 5px;
        display: inline-block;
        width: 100%;
        vertical-align: middle;

        .name {
            width: 80%;
        }

        input {
            width: 20%;
            text-align: right;
        }
    }

    .has-margin-above {
        margin-top: 20px;
    }

    .is-loading {
        text-align: center;
        width: 100%;
    }
}

.modal-card-foot {
    justify-content: flex-end;

    .button.is-pushed-left {
        margin-right: auto;
    }
}
</style>
